import React, {useState} from 'react';
import b_ from 'b_';

import {Radio, Radiobox} from 'client/common/components/lego';
import {AudienceTabChoice, TargetingsProps} from 'client/common/components/audience-settings/types';
import {AudienceNoTargeting} from 'client/common/components/audience-settings/audience-targeting-view/audience-no-targeting';
import TargetingComponent from 'client/common/components/audience-settings/targeting-tabs/targeting-tabs-wrapper';

import {AudienceTargeting} from 'client/common/utils/audience';
import {getIsCreativeBox} from 'client/common/utils/box';

import {useUpdateAudienceRequest} from 'client/common/hooks/audience';
import {useInitialAudienceTab} from 'client/common/hooks/audience-targeting';
import {useBoxType} from 'client/common/hooks/box';
import {sendGoal, sendParams} from 'client/common/utils/metrika';

import {SurveySlug, SurveysGoal, SurveysParam} from 'client/common/types';

import I18N from 'assets/i18n/brandlift-project';

const i18n = I18N.surveyEditor;

const b = b_.with('audience-settings');

interface AudenceTargetingChoicesProps extends TargetingsProps {
    slug: SurveySlug;
}

const sendChangeAudienceMetrics = (value: AudienceTabChoice) => {
    sendGoal(SurveysGoal.ChangeAudienceCategory);
    sendParams([SurveysParam.SelectedCategory, value]);
};

export default function AudienceTargetingChoices(props: AudenceTargetingChoicesProps) {
    const {slug, request, categories, interests, onChange, disabled} = props;

    const boxType = useBoxType(slug);
    const initialTab = useInitialAudienceTab();

    const [audienceTab, setAudienceTab] = useState(initialTab);

    const onRequestUpdate = useUpdateAudienceRequest(slug);

    const selectedCountry = AudienceTargeting.getSelectedCountry({request, boxType, categories});

    const isCreativeBox = getIsCreativeBox(boxType);

    const handleChange = (value: AudienceTabChoice) => {
        if (audienceTab !== value) {
            setAudienceTab(value);
            sendChangeAudienceMetrics(value);

            if (value === AudienceTabChoice.Default) {
                void onRequestUpdate({
                    type: AudienceTargeting.DEFAULT_AUDIENCE.type,
                    categories: AudienceTargeting.DEFAULT_CATEGORIES,
                    interests: null,
                    quotas: null
                });
            } else if (value === AudienceTabChoice.Extended) {
                void onRequestUpdate({
                    type: AudienceTargeting.DEFAULT_EXTENDED_AUDIENCE.type,
                    categories: AudienceTargeting.DEFAULT_EXTENDED_AUDIENCE.categories,
                    interests: null,
                    quotas: null
                });
            } else if (value === AudienceTabChoice.Standard) {
                void onRequestUpdate({
                    type: AudienceTargeting.DEFAULT_AUDIENCE.type,
                    categories: [{categoryId: 'geo', value: [selectedCountry]}],
                    interests: null,
                    quotas: null
                });
            }
        }
    };

    return (
        <>
            <Radiobox
                view='default'
                size='m'
                value={audienceTab}
                className={b('audience-choices')}
                disabled={disabled}
                onChange={e => handleChange(e.target.value as AudienceTabChoice)}
            >
                {!isCreativeBox && (
                    <label className={b('audience-item')}>
                        <Radio value={AudienceTabChoice.Default} />
                        <div className={b('audience-item-text')}>
                            <h5 className={b('audience-item-title')}>
                                {i18n.audienceBlock.noTargetingsTitle}
                            </h5>
                            <h6 className={b('audience-item-subtitle')}>
                                {i18n.audienceBlock.noTargetingsDescription}
                            </h6>

                            {audienceTab === AudienceTabChoice.Default && (
                                <AudienceNoTargeting
                                    request={request}
                                    boxType={boxType}
                                    categories={categories}
                                />
                            )}
                        </div>
                    </label>
                )}

                <label className={b('audience-item')}>
                    <Radio value={AudienceTabChoice.Standard} />
                    <div className={b('audience-item-text')}>
                        <h5 className={b('audience-item-title')}>
                            {i18n.audienceBlock.standardTargetingsTitle}
                        </h5>
                        <h6 className={b('audience-item-subtitle')}>
                            {isCreativeBox
                                ? i18n.audienceBlock.standardTargetingsCreativeDescription
                                : i18n.audienceBlock.standardTargetingsDescription}
                        </h6>
                        {/* ключ нужен чтоб кикнуть рендер когда тип аудитории переключается, сброс на дефолт */}
                        <TargetingComponent
                            boxType={boxType}
                            key={request.type}
                            categories={categories}
                            disabled={disabled}
                            isSelected={audienceTab === AudienceTabChoice.Standard}
                            interests={interests}
                            request={request}
                            onChange={onChange}
                            tab={AudienceTabChoice.Standard}
                        />
                    </div>
                </label>

                {AudienceTargeting.isExtendedTabAvailable(boxType) ? (
                    <label className={b('audience-item')}>
                        <Radio value={AudienceTabChoice.Extended} />
                        <div className={b('audience-item-text')}>
                            <h5 className={b('audience-item-title')}>
                                {i18n.audienceBlock.extendedTargetingsTitle}
                            </h5>
                            <h6
                                className={b('audience-item-subtitle')}
                                dangerouslySetInnerHTML={{
                                    __html: isCreativeBox
                                        ? i18n.audienceBlock.extendedTargetingsCreativeDescription
                                        : i18n.audienceBlock.extendedTargetingsDescription
                                }}
                            />
                            {/* ключ нужен чтоб кикнуть рендер когда тип аудитории переключается, сброс на дефолт */}
                            <TargetingComponent
                                boxType={boxType}
                                key={request.type}
                                categories={categories}
                                disabled={disabled}
                                isSelected={audienceTab === AudienceTabChoice.Extended}
                                interests={interests}
                                request={request}
                                onChange={onChange}
                                tab={AudienceTabChoice.Extended}
                            />
                        </div>
                    </label>
                ) : null}
            </Radiobox>
        </>
    );
}
